import { useNavigate } from 'react-router-dom';

/**
 * Custom hook for generic navigation
 * Handles different types of navigation (normal click, middle-click, Ctrl/Cmd+click)
 * and ensures consistent handling of parameters
 */
const useNavigateWithTabSupport = () => {
  const navigate = useNavigate();

  /**
   * Navigate to a specific route with support for opening in a new tab
   * - Left-click: Navigate in the same tab
   * - Middle-click: Open in a new tab
   * - Ctrl/Cmd+click: Open in a new tab
   *
   *
   * @param {Event} event - The mouse event
   * @param {string} basePath - The base path for navigation (e.g., '/jobs', '/users')
   * @param {string} id - The ID to append to the base path
   * @param {Object} stateParams - Parameters to include in the navigation state
   * @param {Object} queryParams - Parameters to include as query parameters
   * @param {Object} options - Additional options
   * @param {boolean} options.handleMiddleClickOnMouseDown - Whether to handle middle click on mouseDown (default: true)
   */
  const navigateWithTabSupport = (event, basePath, id, stateParams = {}, queryParams = {}, options = {}) => {
    const { handleMiddleClickOnMouseDown = true } = options;
    // Ignore right-clicks (button 2)
    if (event.button === 2) {
      return;
    }

    // Construct the path
    const path = id ? `${basePath}/${id}` : basePath;

    // Build query string from queryParams
    const queryString = Object.entries(queryParams)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    const fullPath = queryString ? `${path}?${queryString}` : path;

    // Determine if we should handle this event based on the event type and options
    const isMiddleClick = event.button === 1;
    const isCtrlClick = (event.ctrlKey || event.metaKey) && event.button === 0;
    const isMouseDown = event.type === 'mousedown';
    const isMouseUp = event.type === 'mouseup';

    // Handle middle-click on mouseDown if specified, otherwise handle on any event
    const shouldHandleMiddleClick = isMiddleClick && (handleMiddleClickOnMouseDown ? isMouseDown : true);

    // Only handle Ctrl/Cmd+click on mouseUp to ensure it only triggers when the mouse button is released
    const shouldHandleCtrlClick = isCtrlClick && isMouseUp;

    if (shouldHandleMiddleClick || shouldHandleCtrlClick) {
      // Prevent default behavior to avoid both behaviors happening
      event.preventDefault();

      // Open in a new tab with absolute URL
      const url = `${window.location.origin}${fullPath}`;
      window.open(url, '_blank');
    } else if (event.button === 0 && !isCtrlClick) {
      // Normal left-click - navigate in the same tab
      navigate(path, { state: stateParams });
    }
  };

  return { navigateWithTabSupport };
};

export default useNavigateWithTabSupport;
